import { isEqual } from "es-toolkit";

import type { DesignerNodeData } from "../../schemas/index.ts";
import type { Design } from "../types.ts";
import { nodeDataCacheProfiler } from "./nodeDataCacheProfiling.ts";

interface CacheEntry {
  childRefs: readonly DesignerNodeData[];
  childNodesKeys: readonly string[];
  result: DesignerNodeData;
}

// AI generated and tweaked only slightly

/**
 * Cache for DesignerNodeData that uses child reference equality to determine cache validity.
 *
 * This cache leverages Immer's structural sharing: when a descendant changes, all ancestors
 * get new child references through recursive rebuilding. By comparing child references,
 * we can determine if the parent actually changed without expensive tree traversal.
 *
 * How it works:
 * - Always rebuilds childNodes by calling children recursively (ensures fresh data)
 * - Compares childNodes keys (to detect added/removed slots)
 * - Compares returned child references with cached child references
 * - If keys match AND all child references identical → returns cached parent
 * - Otherwise → returns new parent
 *
 * Benefits:
 * - Correctness: Always calls children, so grandchild changes propagate up
 * - Performance: Stable parent references when descendants unchanged (prevents re-renders)
 * - Simplicity: Just key comparison + reference comparison
 */
export class NodeDataCache {
  private readonly cache = new WeakMap<Design, CacheEntry>();

  /**
   * Flatten childNodes object into array of DesignerNodeData references.
   * Handles both single nodes and arrays of nodes.
   *
   * @param childNodes - The childNodes object from DesignerNodeData
   * @returns Flat array of all child DesignerNodeData references
   */
  private flattenChildRefs(
    childNodes: Record<string, DesignerNodeData | DesignerNodeData[] | undefined>,
  ): DesignerNodeData[] {
    const PROFILE_started = nodeDataCacheProfiler.enabled ? performance.now() : 0;
    const childRefs: DesignerNodeData[] = [];
    for (const valueOrValues of Object.values(childNodes)) {
      if (Array.isArray(valueOrValues)) {
        childRefs.push(...valueOrValues);
      } else if (valueOrValues) {
        childRefs.push(valueOrValues);
      }
    }
    if (nodeDataCacheProfiler.enabled) {
      const PROFILE_duration = performance.now() - PROFILE_started;
      nodeDataCacheProfiler.recordFlatten(PROFILE_duration, childRefs.length);
    }
    return childRefs;
  }

  /**
   * Get cached result if childNodes keys and all child references are identical.
   *
   * @param design - The Design object to use as cache key
   * @param childNodes - The childNodes object to compare
   * @returns Cached DesignerNodeData if structure and children unchanged, undefined otherwise
   */
  getCached(
    design: Design,
    childNodes: Record<string, DesignerNodeData | DesignerNodeData[] | undefined>,
  ): DesignerNodeData | undefined {
    const PROFILE_getStart = nodeDataCacheProfiler.enabled ? performance.now() : 0;
    const cached = this.cache.get(design);
    if (!cached) {
      if (nodeDataCacheProfiler.enabled) {
        nodeDataCacheProfiler.recordGet(false, performance.now() - PROFILE_getStart);
      }
      return undefined;
    }

    // Check if childNodes structure (keys) changed
    const keys = Object.keys(childNodes).sort();
    if (!isEqual(keys, cached.childNodesKeys)) {
      if (nodeDataCacheProfiler.enabled) {
        nodeDataCacheProfiler.recordGet(false, performance.now() - PROFILE_getStart);
      }
      return undefined;
    }

    const childRefs = this.flattenChildRefs(childNodes);

    // Check if all child references are identical
    if (cached.childRefs.length !== childRefs.length) {
      if (nodeDataCacheProfiler.enabled) {
        nodeDataCacheProfiler.recordGet(false, performance.now() - PROFILE_getStart);
      }
      return undefined;
    }

    for (let i = 0; i < childRefs.length; i++) {
      if (cached.childRefs[i] !== childRefs[i]) {
        if (nodeDataCacheProfiler.enabled) {
          nodeDataCacheProfiler.recordGet(false, performance.now() - PROFILE_getStart);
        }
        return undefined;
      }
    }
    if (nodeDataCacheProfiler.enabled) {
      nodeDataCacheProfiler.recordGet(true, performance.now() - PROFILE_getStart);
    }
    return cached.result;
  }

  /**
   * Store result in cache with childNodes keys and child references.
   *
   * @param design - The Design object to use as cache key
   * @param childNodes - The childNodes object to store
   * @param result - The DesignerNodeData to cache
   */
  set(
    design: Design,
    childNodes: Record<string, DesignerNodeData | DesignerNodeData[] | undefined>,
    result: DesignerNodeData,
  ): void {
    const PROFILE_setStart = nodeDataCacheProfiler.enabled ? performance.now() : 0;
    const childRefs = this.flattenChildRefs(childNodes);
    const childNodesKeys = Object.keys(childNodes).sort();
    this.cache.set(design, { childRefs, childNodesKeys, result });
    if (nodeDataCacheProfiler.enabled) {
      nodeDataCacheProfiler.recordSet(performance.now() - PROFILE_setStart, design);
    }
  }

  /**
   * Clear all entries from the cache.
   * Useful for testing or when you need to force cache invalidation.
   */
  clear(): void {
    // WeakMap doesn't have a clear method, so we create a new one
    // This is primarily useful for testing
    Object.setPrototypeOf(this.cache, WeakMap.prototype);
  }
}
